import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const COLS = [
  { title: "Company", links: [["Experiences", "/experiences"], ["VR Games", "/games"], ["XR Production", "/studio"], ["Distribution", "/distribution"], ["Locations", "/locations"]] },
  { title: "Partners", links: [["For XR Creators", "/creators"], ["For Venues", "/venues"], ["Careers", "/about#careers"], ["News", "/insights"], ["Contact", "/contact"]] },
  { title: "Legal", links: [["Privacy Policy", "/legal/privacy"], ["Terms & Conditions", "/legal/terms"], ["Cookie Policy", "/legal/cookies"]] },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.subscribe(email);
      toast.success("You're on the list. Welcome to the future of entertainment.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.06] bg-[#020204]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="flex items-baseline gap-1 mb-6">
              <span className="font-serif text-3xl tracking-[0.08em]">VROVA</span>
              <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
            </div>
            <p className="text-zinc-400 font-light leading-relaxed max-w-sm mb-10">
              Create immersive worlds. Bring the world's best XR experiences to new audiences.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="flex max-w-sm">
              <input
                data-testid="newsletter-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for immersive updates"
                className="rounded-r-none border-r-0"
              />
              <button data-testid="newsletter-submit-button" disabled={busy}
                className="shrink-0 px-5 rounded-r-lg bg-white text-black hover:bg-zinc-200 transition-colors duration-300 flex items-center">
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
          {COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-6">{col.title}</p>
              <ul className="space-y-3.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} data-testid={`footer-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 font-light">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-3">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-6">Contact</p>
            <a href="https://vrova.in" data-testid="footer-domain" className="block text-sm text-zinc-400 hover:text-white transition-colors mb-3">vrova.in</a>
            <a href="https://wa.me/916281128057" target="_blank" rel="noreferrer" data-testid="footer-whatsapp"
              className="block text-sm text-zinc-400 hover:text-white transition-colors">
              WhatsApp: +91 62811 28057
            </a>
            <p className="text-sm text-zinc-500 mt-3 font-light">Hyderabad, India</p>
          </div>
        </div>
        <div className="mt-20 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row justify-between gap-4">
          <p className="text-xs text-zinc-600 font-mono">© {new Date().getFullYear()} Vrova. All rights reserved.</p>
          <p className="text-xs text-zinc-600 font-mono">Immersive Entertainment · XR Production · Global Distribution</p>
        </div>
      </div>
    </footer>
  );
};
